import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, AlertTriangle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CollectionDataTable } from './CollectionDataTable';
import { columns, editCollectionEvent, deleteCollectionEvent } from './columns';
import { 
  listItemCollections, 
  deleteItemCollection 
} from '@/app/services/itemCollectionService';
import { ItemCollection } from '@/app/types/itemCollection';
import { useOrganization } from '@/app/hooks/useOrganization';
import { useDebounce } from '@/hooks/useDebounce';

const ItemCollectionsListPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();
  const navigate = useNavigate();
  
  // State for search, dialogs
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 300);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<ItemCollection | null>(null);

  // Fetch item collections
  const { 
    data: collectionsData = [], 
    isLoading, 
    isError, 
    error 
  } = useQuery<ItemCollection[]>({
    queryKey: ['item-collections', currentOrganizationId, debouncedSearch],
    queryFn: () => {
      const params: any = {};
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      return listItemCollections(params);
    },
    enabled: !!currentOrganizationId,
  });

  // Filter collections based on search
  const filteredCollections = React.useMemo(() => {
    if (!debouncedSearch) return collectionsData;
    
    return collectionsData.filter((collection) => {
      const searchLower = debouncedSearch.toLowerCase();
      return (
        collection.name.toLowerCase().includes(searchLower) ||
        collection.slug.toLowerCase().includes(searchLower) ||
        (collection.description && collection.description.toLowerCase().includes(searchLower))
      );
    });
  }, [collectionsData, debouncedSearch]);

  // Delete mutation
  const deleteCollectionMutation = useMutation({
    mutationFn: (id: number) => deleteItemCollection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-collections'] });
      setIsDeleteAlertOpen(false);
      setSelectedCollection(null);
      toast.success('Collection deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete collection: ${error.message}`);
    },
  });

  // Event handlers for table actions
  useEffect(() => {
    const handleEditCollection = (event: CustomEvent<{ collection: ItemCollection }>): void => {
      navigate(`/items/collections/detail/${event.detail.collection.id}`);
    };

    const handleDeleteCollection = (event: CustomEvent<{ collection: ItemCollection }>): void => {
      setSelectedCollection(event.detail.collection);
      setIsDeleteAlertOpen(true);
    };

    document.addEventListener(editCollectionEvent, handleEditCollection as EventListener);
    document.addEventListener(deleteCollectionEvent, handleDeleteCollection as EventListener);

    return () => {
      document.removeEventListener(editCollectionEvent, handleEditCollection as EventListener);
      document.removeEventListener(deleteCollectionEvent, handleDeleteCollection as EventListener);
    };
  }, [navigate]);

  // Handle search input change
  const handleSearchChange = useCallback((value: string): void => {
    setSearch(value);
  }, []);

  // Handle delete confirmation
  const handleDeleteCollectionConfirm = (): void => {
    if (selectedCollection) {
      deleteCollectionMutation.mutate(selectedCollection.id);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Item Collections</h1>
          <p className="text-muted-foreground">
            Manage curated product lists and collections
          </p>
        </div>
        <Link to="/items/collections/detail">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Collection
          </Button>
        </Link>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading collections: {(error as Error)?.message || 'An unknown error occurred'}
          </AlertDescription>
        </Alert>
      )}

      {/* Collections Data Table */}
      <CollectionDataTable
        data={filteredCollections}
        columns={columns}
        isLoading={isLoading}
        searchValue={search}
        onSearchChange={handleSearchChange}
      />

      {/* Delete Confirmation Alert */}
      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the collection &quot;{selectedCollection?.name}&quot;. 
              This action cannot be undone.
              {selectedCollection?.items && selectedCollection.items.length > 0 && (
                <span className="block mt-2 text-amber-600">
                  Warning: This collection contains {selectedCollection.items.length} item(s).
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCollectionConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteCollectionMutation.isPending}
            >
              {deleteCollectionMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ItemCollectionsListPage;